/**
 * The working-hours gate, as the settings surface sees it.
 *
 * Moved out of `lib/telephony.ts` unchanged when that file reached the size
 * AGENTS.md section 4 sets, and `lib/telephony.ts` re-exports every name below
 * so not one import anywhere had to change. Same move `telephonyNotice.ts`
 * already made, for the same reason.
 *
 * It is one subject rather than a slice of lines: when the number is answered
 * by a person, what a caller outside those hours is told instead, and the
 * preview that answers both questions for a schedule still being edited.
 *
 * A HAND-WRITTEN MIRROR of the server's `TelephonyHours` with nothing checking
 * it: a field missed here compiles, renders and silently never saves.
 */
import { api } from "./api";
import type { NoticeVariant } from "./telephonyNotice";

/**
 * One weekday of the working-hours schedule, in minutes from local midnight.
 *
 * Indexed the way `Date.getDay()` indexes weekdays - 0 is Sunday, 6 is Saturday
 * - because the server reads this array against a clock. The editor draws them
 * Monday-first with one ordering constant rather than converting the storage.
 */
export interface BusinessDay {
  open: boolean;
  from: number;
  to: number;
}

/** Mirrors the server's `TelephonyHours`. */
export interface TelephonyHoursSettings {
  /** Apply the schedule at all. Off means the number answers the same at 3am. */
  enabled: boolean;
  /** IANA zone the times are read in. */
  timezone: string;
  /** Seven entries, Sunday first. */
  days: BusinessDay[];
  /** Take a message after hours instead of ending the call. */
  voicemail: boolean;
  voicemailPrompt: NoticeVariant;
  closedPrompt: NoticeVariant;
}

/**
 * What a schedule that is still being edited would mean, answered by the server.
 *
 * The same reason `previewRetention` exists: the question is about a policy that
 * has not been saved, so reading the stored settings would describe the hours
 * somebody has just finished changing. The spoken sentence comes from here too,
 * so the wording the button fills in and the check that notices it has gone
 * stale can never be two implementations.
 */
export interface HoursPreview {
  /** The zone actually used, after the server dropped anything Intl refuses. */
  timezone: string;
  /** The clock the server reads in that zone right now, "HH:MM". */
  localTime: string;
  /** `Date.getDay()` index of today, in that zone. */
  weekday: number;
  /** Is the schedule open at this moment, whether or not the gate is switched on? */
  openNow: boolean;
  /** The opening hours as one spoken sentence, "" when nothing is open. */
  schedule: string;
  /** The full announcement for a call that will take a message... */
  voicemailText: string;
  /** ...and for one that will not. */
  closedText: string;
  /** The wording in the form still names a different schedule. */
  voicemailDrifted: boolean;
  closedDrifted: boolean;
}

/**
 * A change to the working-hours gate.
 *
 * `days` is sent whole or not at all: a per-index merge on the server would make
 * "I unticked Saturday" and "my form only knows about six days" arrive looking
 * the same.
 */
export type TelephonyHoursPatch = Partial<{
  enabled: boolean;
  timezone: string;
  days: BusinessDay[];
  voicemail: boolean;
  voicemailPrompt: Partial<NoticeVariant>;
  closedPrompt: Partial<NoticeVariant>;
}>;

/**
 * What the schedule on screen would mean, asked before it is saved.
 *
 * Read-only despite the POST: seven day ranges and two wordings do not fit in a
 * query string. Everything it answers - the clock in the chosen zone, whether
 * the line is open right now, the spoken sentence, and whether the wording in
 * the form still names some other schedule - is worked out from the body it is
 * handed, so nothing here reads or writes the stored row.
 */
export function previewHours(hours: TelephonyHoursPatch): Promise<HoursPreview> {
  return api.post("/admin/crm/calls/hours/preview", hours);
}
